import React, { useState, useEffect, useRef, useContext, useCallback } from "react";
import { toast } from "react-toastify";
import Dialog from "@material-ui/core/Dialog";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import CircularProgress from "@material-ui/core/CircularProgress";
import TextField from "@material-ui/core/TextField";
import { Checkbox } from "@mui/material";
import { AccessTime, Image, Message, MicNone, Videocam, Description } from "@mui/icons-material";
import Compressor from "compressorjs";
import api from "../../services/api";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";

// ── helpers ────────────────────────────────────────────────────────────────
const uid = () => Math.random().toString(36).slice(2, 8);

const TYPE_COLOR = {
  message: { bg: "#eff6ff", border: "#bfdbfe", badge: "#3b82f6", label: "TEXTO" },
  interval: { bg: "#fffbeb", border: "#fde68a", badge: "#f59e0b", label: "INTERVALO" },
  img: { bg: "#f5f3ff", border: "#ddd6fe", badge: "#8b5cf6", label: "IMAGEM" },
  audio: { bg: "#f0fdf4", border: "#bbf7d0", badge: "#10b981", label: "ÁUDIO" },
  video: { bg: "#fdf2f8", border: "#f9a8d4", badge: "#ec4899", label: "VÍDEO" },
  document: { bg: "#f8fafc", border: "#cbd5e1", badge: "#64748b", label: "DOCUMENTO" },
};

const TypeIcon = ({ type }) => {
  if (type === "message") return <Message sx={{ fontSize: 12 }} />;
  if (type === "interval") return <AccessTime sx={{ fontSize: 12 }} />;
  if (type === "img") return <Image sx={{ fontSize: 12 }} />;
  if (type === "audio") return <MicNone sx={{ fontSize: 12 }} />;
  if (type === "video") return <Videocam sx={{ fontSize: 12 }} />;
  return <Description sx={{ fontSize: 12 }} />;
};

const DEFAULT_VARIABLES = [
  { key: "primeiro_nome", label: "Primeiro nome do contato" },
  { key: "name", label: "Nome completo do contato" },
  { key: "ms", label: "Saudação (Bom dia, Boa tarde...)" },
  { key: "protocol", label: "Número do protocolo" },
  { key: "date", label: "Data atual" },
  { key: "hour", label: "Hora atual" },
  { key: "produto", label: "🛒 Hotmart: Nome do produto" },
  { key: "pix", label: "📋 Hotmart: Código Pix (copia e cola)" },
  { key: "link_pix", label: "🖼️ Hotmart: QR Code do Pix" },
];

// ── main component ─────────────────────────────────────────────────────────
const FlowBuilderSingleBlockModal = ({ open, onSave, onUpdate, data, close }) => {
  const { whatsApps } = useContext(WhatsAppsContext);
  const bodyRef = useRef(null);
  const fileRefs = useRef({});

  const connectedWAs = whatsApps ? whatsApps.filter((w) => w.status === "CONNECTED") : [];

  // State — elements is array of { id, type, value, record, fileName, fileUrl, fileBlob }
  const [elements, setElements] = useState([]);
  const [activeModal, setActiveModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedWAId, setSelectedWAId] = useState(null);
  const [nodeName, setNodeName] = useState("");
  const [activeTextId, setActiveTextId] = useState(null);
  const [pendingFiles, setPendingFiles] = useState({}); // id -> File object
  const isEdit = open === "edit";
  const [dragOverId, setDragOverId] = useState(null);
  const dragItemId = useRef(null);

  // ── open/close ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    setSelectedWAId(null);
    setNodeName("");
    setElements([]);
    setPendingFiles({});
    fileRefs.current = {};

    if (open === "edit" && data?.data) {
      setSelectedWAId(data.data.whatsappId || null);
      setNodeName(data.data.nodeLabel || "");
      const seq = data.data.seq || [];
      const elems = data.data.elements || [];
      const loaded = seq.map((num) => {
        const el = elems.find((e) => e.number === num) || {};
        return {
          id: num,
          type: el.type || "message",
          value: el.value || "",
          original: el.original || "",
          record: el.record !== false,
          fileName: el.original || "",
        };
      });
      setElements(loaded);
    }
    setActiveModal(true);
  }, [open]);

  const handleClose = () => {
    close(null);
    setActiveModal(false);
    setTimeout(() => {
      setElements([]);
      setPendingFiles({});
      setSelectedWAId(null);
      setNodeName("");
    }, 400);
  };

  const scrollToBottom = () => {
    setTimeout(() => {
      if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }, 80);
  };

  // ── add element ─────────────────────────────────────────────────────────
  const addElement = (type) => {
    const defaults = {
      message: { value: "" },
      interval: { value: "3" },
      img: { value: "", fileName: "" },
      audio: { value: "", fileName: "", record: true },
      video: { value: "", fileName: "" },
      document: { value: "", fileName: "" },
    };
    const id = type + uid();
    setElements((old) => [...old, { id, type, ...defaults[type] }]);
    scrollToBottom();
  };

  const removeElement = (id) => {
    setElements((old) => old.filter((e) => e.id !== id));
    setPendingFiles((old) => { const n = { ...old }; delete n[id]; return n; });
  };

  // ── drag-and-drop reorder ───────────────────────────────────────────────
  const handleDragStart = (e, id) => {
    dragItemId.current = id;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", id);
  };

  const handleDragOver = (e, id) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverId !== id) setDragOverId(id);
  };

  const handleDrop = (e, targetId) => {
    e.preventDefault();
    const fromId = dragItemId.current;
    if (!fromId || fromId === targetId) { setDragOverId(null); return; }
    setElements((old) => {
      const arr = [...old];
      const fromIdx = arr.findIndex((el) => el.id === fromId);
      const toIdx = arr.findIndex((el) => el.id === targetId);
      if (fromIdx === -1 || toIdx === -1) return arr;
      const [item] = arr.splice(fromIdx, 1);
      arr.splice(toIdx, 0, item);
      return arr;
    });
    setDragOverId(null);
    dragItemId.current = null;
  };

  const handleDragEnd = () => {
    setDragOverId(null);
    dragItemId.current = null;
  };

  const updateElement = (id, patch) => {
    setElements((old) => old.map((e) => e.id === id ? { ...e, ...patch } : e));
  };

  // ── file handlers ───────────────────────────────────────────────────────
  const handleFileChange = (e, id, type) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const limits = { img: 2e6, audio: 5e6, video: 20e6, document: 15e6 };
    const labels = { img: "2MB", audio: "5MB", video: "20MB", document: "15MB" };
    if (file.size > limits[type]) { toast.error(`Arquivo muito grande! Máximo ${labels[type]}`); return; }
    const url = URL.createObjectURL(file);
    updateElement(id, { fileName: file.name, fileUrl: url });
    setPendingFiles((old) => ({ ...old, [id]: file }));
  };

  // ── variable insertion ──────────────────────────────────────────────────
  const insertVariable = (key) => {
    const token = `{{${key}}} `;
    const targetId = activeTextId ?? elements.find((e) => e.type === "message")?.id;
    if (!targetId) { toast.info("Adicione um bloco de texto primeiro."); return; }
    const ref = fileRefs.current[targetId];
    if (!ref) return;
    const start = ref.selectionStart ?? ref.value.length;
    const end = ref.selectionEnd ?? ref.value.length;
    const newVal = ref.value.slice(0, start) + token + ref.value.slice(end);
    updateElement(targetId, { value: newVal });
    setTimeout(() => {
      if (ref) { ref.focus(); try { ref.setSelectionRange(start + token.length, start + token.length); } catch (_) {} }
    }, 20);
  };

  // ── save ────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    // Validate
    for (const el of elements) {
      if (el.type === "message" && !el.value.trim()) {
        toast.error("Preencha todos os campos de texto."); return;
      }
      if (el.type === "interval") {
        const v = parseInt(el.value);
        if (!v || v <= 0 || v > 120) { toast.error("Intervalo deve ser entre 1 e 120 segundos."); return; }
      }
    }

    setLoading(true);

    // Upload files if any
    const filesToUpload = Object.entries(pendingFiles);
    let uploadedNames = {};

    if (filesToUpload.length > 0) {
      const formData = new FormData();
      for (const [id, file] of filesToUpload) {
        if (file.type.startsWith("image/")) {
          await new Promise((resolve) => {
            new Compressor(file, {
              quality: 0.7,
              success(compressed) { formData.append("medias", compressed); formData.append("body", compressed.name); resolve(); },
              error() { formData.append("medias", file); formData.append("body", file.name); resolve(); },
            });
          });
        } else {
          formData.append("medias", file);
          formData.append("body", file.name);
        }
      }
      try {
        const res = await api.post("/flowbuilder/content", formData);
        // res.data is array of uploaded filenames in order
        filesToUpload.forEach(([id], i) => { uploadedNames[id] = res.data[i]; });
      } catch (err) {
        toast.error("Erro ao enviar arquivos."); setLoading(false); return;
      }
    }

    // Build seq + elements
    const seq = elements.map((e) => e.id);
    const elems = elements.map((e) => {
      const base = { type: e.type, number: e.id };
      if (e.type === "message") return { ...base, value: e.value };
      if (e.type === "interval") return { ...base, value: e.value };
      if (e.type === "audio") return { ...base, value: uploadedNames[e.id] || e.value, original: e.fileName, record: e.record };
      return { ...base, value: uploadedNames[e.id] || e.value, original: e.fileName };
    });

    const mountData = {
      seq,
      elements: elems,
      whatsappId: selectedWAId || undefined,
      nodeLabel: nodeName || undefined,
    };

    if (isEdit) {
      onUpdate({ ...data, data: mountData });
    } else {
      onSave(mountData);
    }

    toast.success("Conteúdo salvo!");
    handleClose();
    setLoading(false);
  };

  // ── render element card ─────────────────────────────────────────────────
  const renderCard = (el, idx) => {
    const c = TYPE_COLOR[el.type] || TYPE_COLOR.message;
    const isDragTarget = dragOverId === el.id;
    return (
      <div
        key={el.id}
        draggable
        onDragStart={(e) => handleDragStart(e, el.id)}
        onDragOver={(e) => handleDragOver(e, el.id)}
        onDrop={(e) => handleDrop(e, el.id)}
        onDragEnd={handleDragEnd}
        style={{
          background: c.bg,
          border: isDragTarget ? "2px dashed " + c.badge : "1px solid " + c.border,
          borderRadius: 12, padding: "12px 14px", position: "relative",
          opacity: dragItemId.current === el.id ? 0.5 : 1,
          transition: "border-color 0.15s ease, opacity 0.15s ease",
          cursor: "grab",
        }}
      >
        {/* Card header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {/* Drag handle */}
            <div
              title="Arrastar para reordenar"
              style={{
                display: "flex", flexDirection: "column", gap: "3px",
                padding: "4px 5px", cursor: "grab", opacity: 0.45,
                borderRadius: 6, userSelect: "none",
              }}
              onMouseEnter={(e) => e.currentTarget.style.opacity = "1"}
              onMouseLeave={(e) => e.currentTarget.style.opacity = "0.45"}
            >
              {[0,1,2].map(i => (
                <div key={i} style={{ display: "flex", gap: "3px" }}>
                  <div style={{ width: 3.5, height: 3.5, borderRadius: "50%", background: c.badge }} />
                  <div style={{ width: 3.5, height: 3.5, borderRadius: "50%", background: c.badge }} />
                </div>
              ))}
            </div>
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 5,
              fontSize: 10.5, fontWeight: 700, color: c.badge,
              background: c.badge + "18", borderRadius: 20, padding: "3px 10px",
              textTransform: "uppercase", letterSpacing: "0.06em",
            }}>
              <TypeIcon type={el.type} /> {c.label}
              {elements.length > 1 && (
                <span style={{ opacity: 0.6, fontSize: 9, marginLeft: 2 }}>#{idx + 1}</span>
              )}
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            {/* Move up */}
            {idx > 0 && (
              <button
                onClick={() => setElements(old => {
                  const arr = [...old];
                  [arr[idx - 1], arr[idx]] = [arr[idx], arr[idx - 1]];
                  return arr;
                })}
                title="Mover para cima"
                style={{
                  width: 26, height: 26, borderRadius: "50%", background: c.badge + "18",
                  border: "none", cursor: "pointer", color: c.badge,
                  display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13,
                }}
              >▲</button>
            )}
            {/* Move down */}
            {idx < elements.length - 1 && (
              <button
                onClick={() => setElements(old => {
                  const arr = [...old];
                  [arr[idx], arr[idx + 1]] = [arr[idx + 1], arr[idx]];
                  return arr;
                })}
                title="Mover para baixo"
                style={{
                  width: 26, height: 26, borderRadius: "50%", background: c.badge + "18",
                  border: "none", cursor: "pointer", color: c.badge,
                  display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13,
                }}
              >▼</button>
            )}
            <button onClick={() => removeElement(el.id)} style={{
              width: 28, height: 28, borderRadius: "50%", background: "#fee2e2",
              border: "none", cursor: "pointer", color: "#ef4444",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                <path d="M9 3V4H4V6H5V20C5 21.1 5.9 22 7 22H17C18.1 22 19 21.1 19 20V6H20V4H15V3H9ZM7 6H17V20H7V6ZM9 8V18H11V8H9ZM13 8V18H15V8H13Z"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Text */}
        {el.type === "message" && (
          <textarea
            ref={(r) => { fileRefs.current[el.id] = r; }}
            value={el.value}
            onChange={(e) => updateElement(el.id, { value: e.target.value })}
            onFocus={() => setActiveTextId(el.id)}
            placeholder="Digite a mensagem..."
            rows={5}
            style={{
              width: "100%", padding: "10px 12px", borderRadius: 10, resize: "vertical",
              border: "1px solid rgba(59,130,246,0.25)", fontSize: 13.5, lineHeight: 1.6,
              fontFamily: "Inter, system-ui, sans-serif", outline: "none",
              background: "#fff", color: "#0f172a", boxSizing: "border-box",
            }}
            onFocus={(e) => { setActiveTextId(el.id); e.target.style.borderColor = "#3b82f6"; }}
            onBlur={(e) => { e.target.style.borderColor = "rgba(59,130,246,0.25)"; }}
          />
        )}

        {/* Interval */}
        {el.type === "interval" && (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <input
              type="number"
              value={el.value}
              min="1" max="120"
              onChange={(e) => updateElement(el.id, { value: e.target.value })}
              style={{
                width: 100, padding: "8px 12px", borderRadius: 10,
                border: "1px solid rgba(245,158,11,0.30)", fontSize: 15, fontWeight: 700,
                color: "#92400e", background: "#fffbeb", outline: "none", textAlign: "center",
              }}
            />
            <span style={{ fontSize: 13, color: "#92400e" }}>segundos (máx. 120)</span>
          </div>
        )}

        {/* Image */}
        {el.type === "img" && (
          <div>
            {(el.fileUrl || el.value) && (
              <div style={{ textAlign: "center", marginBottom: 10 }}>
                <img
                  src={el.fileUrl || (el.value ? process.env.REACT_APP_BACKEND_URL + "/public/" + el.value : "")}
                  style={{ maxWidth: 200, borderRadius: 8 }}
                  alt=""
                />
              </div>
            )}
            <label style={{
              display: "block", textAlign: "center", padding: "9px 16px",
              borderRadius: 10, border: "1.5px solid #8b5cf6", color: "#8b5cf6",
              cursor: "pointer", fontSize: 13, fontWeight: 600, background: "#faf5ff",
            }}>
              {el.fileName || el.value ? "Trocar imagem" : "Enviar imagem"}
              <input type="file" accept="image/png,image/jpg,image/jpeg" hidden onChange={(e) => handleFileChange(e, el.id, "img")} />
            </label>
            {el.fileName && <div style={{ fontSize: 11, color: "#7c3aed", textAlign: "center", marginTop: 5 }}>{el.fileName}</div>}
            <div style={{ fontSize: 11, color: "#9ca3af", textAlign: "center", marginTop: 4 }}>Máximo 2MB</div>
          </div>
        )}

        {/* Audio */}
        {el.type === "audio" && (
          <div>
            {(el.fileUrl || el.value) && (
              <div style={{ marginBottom: 10, display: "flex", justifyContent: "center" }}>
                <audio controls src={el.fileUrl || (el.value ? process.env.REACT_APP_BACKEND_URL + "/public/" + el.value : "")} />
              </div>
            )}
            <label style={{
              display: "block", textAlign: "center", padding: "9px 16px",
              borderRadius: 10, border: "1.5px solid #10b981", color: "#10b981",
              cursor: "pointer", fontSize: 13, fontWeight: 600, background: "#f0fdf4",
            }}>
              {el.fileName || el.value ? "Trocar áudio" : "Enviar áudio"}
              <input type="file" accept="audio/ogg,audio/mp3,audio/opus" hidden onChange={(e) => handleFileChange(e, el.id, "audio")} />
            </label>
            {el.fileName && <div style={{ fontSize: 11, color: "#059669", textAlign: "center", marginTop: 5 }}>{el.fileName}</div>}
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8 }}>
              <Checkbox
                checked={el.record !== false}
                onChange={(e) => updateElement(el.id, { record: e.target.checked })}
                size="small"
                style={{ color: "#10b981", padding: 4 }}
              />
              <span style={{ fontSize: 12, color: "#374151" }}>Enviar como áudio gravado na hora</span>
            </div>
          </div>
        )}

        {/* Video */}
        {el.type === "video" && (
          <div>
            {(el.fileUrl || el.value) && (
              <div style={{ marginBottom: 10, display: "flex", justifyContent: "center" }}>
                <video controls style={{ maxWidth: 200, borderRadius: 8 }}>
                  <source src={el.fileUrl || (el.value ? process.env.REACT_APP_BACKEND_URL + "/public/" + el.value : "")} />
                </video>
              </div>
            )}
            <label style={{
              display: "block", textAlign: "center", padding: "9px 16px",
              borderRadius: 10, border: "1.5px solid #ec4899", color: "#ec4899",
              cursor: "pointer", fontSize: 13, fontWeight: 600, background: "#fdf2f8",
            }}>
              {el.fileName || el.value ? "Trocar vídeo" : "Enviar vídeo"}
              <input type="file" accept="video/mp4" hidden onChange={(e) => handleFileChange(e, el.id, "video")} />
            </label>
            {el.fileName && <div style={{ fontSize: 11, color: "#be185d", textAlign: "center", marginTop: 5 }}>{el.fileName}</div>}
          </div>
        )}

        {/* Document */}
        {el.type === "document" && (
          <div>
            <label style={{
              display: "block", textAlign: "center", padding: "9px 16px",
              borderRadius: 10, border: "1.5px solid #64748b", color: "#64748b",
              cursor: "pointer", fontSize: 13, fontWeight: 600, background: "#f8fafc",
            }}>
              {el.fileName || el.value ? "Trocar documento" : "Enviar documento"}
              <input type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.csv" hidden onChange={(e) => handleFileChange(e, el.id, "document")} />
            </label>
            {(el.fileName || el.value) && (
              <div style={{ fontSize: 12, color: "#475569", textAlign: "center", marginTop: 6, display: "flex", justifyContent: "center", alignItems: "center", gap: 6 }}>
                <Description sx={{ fontSize: 16, color: "#64748b" }} /> {el.fileName || el.original || el.value}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // ── main render ─────────────────────────────────────────────────────────
  return (
    <Dialog
      open={activeModal}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      scroll="paper"
      PaperProps={{ style: { borderRadius: 16, overflow: "hidden", boxShadow: "0 24px 64px rgba(16,24,40,0.20)" } }}
    >
      {loading ? (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "70vh", gap: 12 }}>
          <CircularProgress style={{ color: "#22c55e" }} />
          <div style={{ fontSize: 13, color: "#64748b" }}>Salvando...</div>
        </div>
      ) : (
        <>
          {/* ── Header ── */}
          <div style={{ padding: "16px 20px 14px", borderBottom: "1px solid rgba(15,23,42,0.07)", background: "#fff" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{
                  width: 38, height: 38, borderRadius: 12, flexShrink: 0,
                  background: "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  boxShadow: "0 4px 12px rgba(34,197,94,0.28)",
                }}>
                  <svg width="19" height="19" viewBox="0 0 24 24" fill="white">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                </div>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}>{isEdit ? "Editar conteúdo" : "Adicionar conteúdo"}</div>
                  <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 1 }}>Configure as mensagens e o WhatsApp para envio</div>
                </div>
              </div>
              <IconButton onClick={handleClose} size="small" style={{ width: 34, height: 34, borderRadius: 10, border: "1px solid rgba(15,23,42,0.10)" }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </div>

            {/* Nome + WhatsApp */}
            <div style={{ display: "flex", gap: 10, marginTop: 14, alignItems: "flex-end" }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 10.5, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 5 }}>Nome do bloco (opcional)</div>
                <input
                  value={nodeName}
                  onChange={(e) => setNodeName(e.target.value)}
                  placeholder="Ex: PIX RECUPERACAO 1"
                  style={{
                    width: "100%", padding: "9px 12px", border: "1px solid rgba(15,23,42,0.12)",
                    borderRadius: 10, fontSize: 13, outline: "none", fontFamily: "Inter, system-ui, sans-serif",
                    color: "#0f172a", background: "#f8fafc", boxSizing: "border-box",
                  }}
                  onFocus={(e) => e.target.style.borderColor = "#22c55e"}
                  onBlur={(e) => e.target.style.borderColor = "rgba(15,23,42,0.12)"}
                />
              </div>
              {connectedWAs.length > 0 && (
                <div style={{ minWidth: 200 }}>
                  <div style={{ fontSize: 10.5, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 5 }}>WhatsApp para envio</div>
                  <select
                    value={selectedWAId || ""}
                    onChange={(e) => setSelectedWAId(e.target.value ? Number(e.target.value) : null)}
                    style={{
                      width: "100%", padding: "9px 12px", border: "1px solid rgba(15,23,42,0.12)",
                      borderRadius: 10, fontSize: 13, fontFamily: "Inter, system-ui, sans-serif",
                      color: "#0f172a", background: "#f8fafc", outline: "none", cursor: "pointer",
                    }}
                  >
                    <option value="">Padrão do sistema</option>
                    {connectedWAs.map((wa) => (
                      <option key={wa.id} value={wa.id}>{wa.name}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* ── Body ── */}
          <div
            ref={bodyRef}
            style={{
              padding: "14px 16px",
              overflowY: "auto",
              height: "52vh",
              display: "flex",
              flexDirection: "column",
              gap: 8,
              background: "#f8fafc",
            }}
          >
            {elements.map(renderCard)}

            {/* Add buttons */}
            <div style={{
              padding: "12px 14px", background: "#fff", borderRadius: 12,
              border: "1px dashed rgba(15,23,42,0.12)",
            }}>
              <div style={{ fontSize: 10.5, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>
                + Adicionar bloco
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {[
                  { type: "message", color: "#3b82f6", icon: <Message sx={{ fontSize: 13 }} />, label: "Texto" },
                  { type: "interval", color: "#f59e0b", icon: <AccessTime sx={{ fontSize: 13 }} />, label: "Intervalo" },
                  { type: "img", color: "#8b5cf6", icon: <Image sx={{ fontSize: 13 }} />, label: "Imagem" },
                  { type: "audio", color: "#10b981", icon: <MicNone sx={{ fontSize: 13 }} />, label: "Áudio" },
                  { type: "video", color: "#ec4899", icon: <Videocam sx={{ fontSize: 13 }} />, label: "Vídeo" },
                  { type: "document", color: "#64748b", icon: <Description sx={{ fontSize: 13 }} />, label: "Documento" },
                ].map((btn) => (
                  <button
                    key={btn.type}
                    onClick={() => addElement(btn.type)}
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 5,
                      padding: "6px 13px", borderRadius: 20, fontSize: 12, fontWeight: 600,
                      cursor: "pointer", border: `1.5px solid ${btn.color}33`,
                      background: `${btn.color}10`, color: btn.color,
                    }}
                  >
                    {btn.icon} {btn.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Variables */}
            <div style={{ background: "#fff", borderRadius: 12, border: "1px solid rgba(15,23,42,0.08)", padding: "12px 14px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: "#0f172a" }}>Variáveis disponíveis</span>
                <span style={{ fontSize: 11, color: "#94a3b8" }}>Clique para inserir no texto</span>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
                {DEFAULT_VARIABLES.map((v) => (
                  <button
                    key={v.key}
                    onClick={() => insertVariable(v.key)}
                    style={{
                      padding: "4px 10px", borderRadius: 20, fontSize: 11.5, fontWeight: 600,
                      cursor: "pointer", border: "1.5px solid rgba(99,102,241,0.25)",
                      background: "rgba(99,102,241,0.07)", color: "#6366f1",
                    }}
                  >
                    {`{{${v.key}}}`}
                  </button>
                ))}
              </div>
              <div style={{ borderTop: "1px solid rgba(15,23,42,0.06)", paddingTop: 8 }}>
                {DEFAULT_VARIABLES.map((v) => (
                  <div key={v.key} style={{ fontSize: 11.5, color: "#64748b", lineHeight: 1.6 }}>
                    <strong style={{ color: "#6366f1" }}>{`{{${v.key}}}`}</strong> — {v.label}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ── Footer ── */}
          <div style={{
            padding: "14px 20px", borderTop: "1px solid rgba(15,23,42,0.07)",
            display: "flex", justifyContent: "flex-end", gap: 10, background: "#fff",
          }}>
            <button onClick={handleClose} style={{
              padding: "9px 20px", borderRadius: 10, fontSize: 13, fontWeight: 600,
              cursor: "pointer", border: "1.5px solid rgba(15,23,42,0.15)", background: "#fff", color: "#374151",
            }}>
              Cancelar
            </button>
            <button onClick={handleSave} style={{
              padding: "9px 24px", borderRadius: 10, fontSize: 13, fontWeight: 600,
              cursor: "pointer", border: "none",
              background: "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
              color: "#fff", boxShadow: "0 4px 14px rgba(34,197,94,0.35)",
            }}>
              {isEdit ? "Salvar" : "Adicionar"}
            </button>
          </div>
        </>
      )}
    </Dialog>
  );
};

export default FlowBuilderSingleBlockModal;
